import { PageSize, ProposalStatus, ExtendedLineItemFormData, ProposalDiscountType, ProposalDiscountCategory } from '@business/entities/proposal';
import { Proposal } from '@modules/proposal/Blocks/types';
import { ProposalDataType } from '@modules/proposal/ProposalBuilderPage';
import { CommonService } from '../common_service';
import { Formatter } from '@core/utils/formatter';
import { Session } from '@modules/auth/session';
import { getCurrentCompany } from '@core/utils/hooks/misc';
import { toMoment } from '@core/utils/time';
import { R } from '@core/utils/r';
import { LineItemFormData, ProposalDiscountFormData } from '@modules/proposal/ProposalBuilderPage/Components/ProposalProductsOverlay';
import { LabourItemResponse } from '@business/entities/order';
import { DealIntervalResponse } from '@business/entities/deal';
import { OrderLineItemData } from '@modules/timeMaterials/AddEditTimeMaterialPage/TimeMaterialForm';
import { EstimateLineItem, TMChangeOrderLineResponse, TMFeeType } from '@business/entities/tm';
import { ChangeOrderLineItemData } from '@modules/timeMaterials/AddEditTMChangeOrderPage/TMChangeOrderForm';
import { PriceFeeFormData } from '@modules/proposal/ProposalBuilderPage/Components/ProposalProductsOverlay/ProposalProductsOverlay';
import { SettingsResponse } from '@business/entities/settings';
import { TmInvoiceSalesTaxData } from '@modules/common/TmInvoiceSalesTax';
import { TaxService } from '../tax_service';

export function getHeight(width: number, pageSize: PageSize, isLandscape = false) {
  if (isLandscape) {
    switch (pageSize) {
      case PageSize.a4:
        return Math.round((width * 8.5) / 11);
      case PageSize.legal:
        return Math.round((width * 8.5) / 14);
      case PageSize.letter:
        return Math.round((width * 8.5) / 11);
    }
  }
  switch (pageSize) {
    case PageSize.a4:
      return Math.round((width * 11) / 8.5);
    case PageSize.legal:
      return Math.round((width * 14) / 8.5);
    case PageSize.letter:
      return Math.round((width * 11) / 8.5);
  }
}

export function allBlockCategories(bottom = false) {
  if (bottom) {
    return [
      Proposal.BlockCategory.text,
      Proposal.BlockCategory.image,
      Proposal.BlockCategory.multimedia,
      Proposal.BlockCategory.list,
      Proposal.BlockCategory.divider,
      Proposal.BlockCategory.pricing,
      Proposal.BlockCategory.signer,
    ];
  }
  return [
    Proposal.BlockCategory.text,
    Proposal.BlockCategory.image,
    Proposal.BlockCategory.multimedia,
    Proposal.BlockCategory.list,
    Proposal.BlockCategory.divider,
    Proposal.BlockCategory.pricing,
    Proposal.BlockCategory.statement,
    Proposal.BlockCategory.quote,
    Proposal.BlockCategory.gallery,
    Proposal.BlockCategory.chart,
    Proposal.BlockCategory.signer,
    Proposal.BlockCategory.blockTemplate,
    Proposal.BlockCategory.universal,
  ];
}

export function getTransformedValues(data: ProposalDataType | null): Record<string, string> {
  if (!data) return {};
  return {
    company_name: data.company?.name || 'Company Name',
    brand_name: data.brand?.name || 'Brand Name',
    user_name: data.user ? CommonService.getFullName(data.user) : 'User Name',
    user_email: data.user?.email || 'your@company.com',
    company_address: 'Address',
    company_address2: 'Address 2',
    company_city: 'City',
    company_state: 'State',
    company_country: 'Country',
    company_zip: 'Zip',
    org_name: data.organization?.name || 'Organization Name',
    contact_first_name: data.contact?.firstName || 'Contact First Name',
    contact_last_name: data.contact?.lastName || 'Contact Last Name',
    contact_name: data.contact ? CommonService.getFullName(data.contact) : 'Contact Name',
    contact_email: data.contact?.email || 'Contact Email',
    contact_address: R.get(data, 'organization.addresses[0].address') || '',
    contact_address2: R.get(data, 'organization.addresses[0].addressLineTwo') || '',
    contact_city: R.get(data, 'organization.addresses[0].city') || '',
    contact_state: R.get(data, 'organization.addresses[0].state') || '',
    contact_country: R.get(data, 'organization.addresses[0].country') || '',
    contact_zip: R.get(data, 'organization.addresses[0].postal') || '',
    contact_website: R.get(data, 'organization.websites[0]') || '',
    total: Formatter.formatCurrency(data.total, 'USD'),
    date_sumitted: Formatter.formatTime(data.dateSumitted),
    date_accepted: data.dateAccepted ? Formatter.formatTime(data.dateAccepted) : '',
  };
}

export function getProposalData(): ProposalDataType {
  const brand = {
    id: '0',
    name: 'Your Brand',
  };

  const contact = {
    id: '0',
    firstName: 'John',
    lastName: 'Doe',
    name: 'John Doe',
    email: 'jon@example.com',
    phone: '(123) 456-7890',
    organization: {
      name: 'ABC Company',
    },
  };
  const company = getCurrentCompany();

  return { company, user: Session.user, brand, total: 0, dateSumitted: toMoment().unix() };
}

export function getAllFontWeight() {
  // list of all font weights from 100 to 100 with their labels
  return [
    { label: 'Thin', value: '100' },
    { label: 'Extra Light', value: '200' },
    { label: 'Light', value: '300' },
    { label: 'Normal', value: '400' },
    { label: 'Medium', value: '500' },
    { label: 'Semi Bold', value: '600' },
    { label: 'Bold', value: '700' },
    { label: 'Extra Bold', value: '800' },
    { label: 'Black', value: '900' },
  ];
}

export function getStatusColor(type: ProposalStatus, isDeleted = false) {
  //   if (isDeleted) {
  //     return 'danger';
  //   }
  if (type === ProposalStatus.sent) {
    return 'success';
  } else if (type === ProposalStatus.accepted) {
    return 'info';
  } else if (type === ProposalStatus.draft) {
    return 'warning';
  } else if (type === ProposalStatus.archived) {
    return 'danger';
  }
}

export function getLabourPrice(item: Partial<LineItemFormData>) {
  const labourItems = item.labourItems ? item.labourItems : [];
  if (labourItems.length) {
    const totalPriceHours = labourItems.reduce((total, item) => {
      const hh = item.hour;
      const pp = item.price;
      const tt = total + R.roundDecimal(hh * pp, 3);
      return R.isNaN(tt) ? 0 : tt;
    }, 0);
    return R.roundDecimal((item.qty ?? 0) * totalPriceHours, 3);
  } else {
    const labour = item.labour ?? 0;
    const labourPrice = item.labourPrice ?? 0;
    const total = R.roundDecimal((item.qty ?? 0) * labour * labourPrice, 3);
    return R.isNaN(total) ? 0 : total;
  }
}

export function getTotalLaborPrice(item: Partial<LineItemFormData>) {
  return (item.packageItems ?? []).reduce((acc, item) => acc + getLabourPrice(item), 0);
}

export function getTotalLaborHours(item: Partial<LineItemFormData>) {
  return (item.packageItems ?? []).reduce(
    (acc, item) => acc + (item.labourItems ?? []).reduce((total, item) => total + R.roundDecimal(item.hour, 3), 0),
    0,
  );
}

export function getTotalQty(item: Partial<LineItemFormData>) {
  return (item.packageItems ?? []).reduce((acc, item) => acc + R.roundDecimal(item.qty, 2), 0);
}

export function getTotalRate(item: Partial<LineItemFormData>) {
  return (item.packageItems ?? []).reduce((acc, item) => acc + R.roundDecimal(convertToValidNumber(item.rate || 0), 3), 0);
}

export function getLineLaborPrice(items: LabourItemResponse[]) {
  return items.reduce((total, item) => total + R.roundDecimal(item.hour * item.price, 3), 0);
}

export function getLineLaborCost(items: LabourItemResponse[]) {
  return items.reduce((total, item) => total + R.roundDecimal(convertToValidNumber(item.cost || 0), 3), 0);
}

export function getLineHours(items: LabourItemResponse[]) {
  return items.reduce((total, item) => total + R.roundDecimal(item.hour, 3), 0);
}

export function getSubTotal(formValue: LineItemFormData, format = true, editable = true) {
  if (formValue.packageItems && formValue.packageItems.length > 0) {
    const t = formValue.packageItems.reduce((acc, item) => acc + (!editable ? item.qty * item.rate : item.qty * item.rate + getLabourPrice(item)), 0);
    const value = format ? Formatter.formatCurrency(t, getCurrentCompany().currencyCode) : t;
    if (formValue.frequency === DealIntervalResponse.onetime) {
      return value;
    } else {
      return `${value}/${formValue.frequency}`;
    }
  } else {
    const t = !editable ? formValue.qty * formValue.rate : formValue.qty * formValue.rate + getLabourPrice(formValue);
    const value = format ? Formatter.formatCurrency(t, getCurrentCompany().currencyCode) : t;
    if (formValue.frequency === DealIntervalResponse.onetime) {
      return value;
    } else {
      return `${value}/${formValue.frequency}`;
    }
  }
}

export function getSubTotalNew(formValue: LineItemFormData, format = true, editable = true, combineLabor = false) {
  if (formValue.packageItems && formValue.packageItems.length > 0) {
    const t = formValue.packageItems.reduce((acc, item) => {
      const base = item.qty * item.rate;
      const labor = combineLabor ? getLabourPrice(item) : 0;
      return acc + (editable ? base : base + labor);
    }, 0);

    const value = format ? Formatter.formatCurrency(t, getCurrentCompany().currencyCode) : t;

    if (formValue.frequency === DealIntervalResponse.onetime) {
      return value;
    } else {
      return `${value}/${formValue.frequency}`;
    }
  } else {
    const base = formValue.qty * formValue.rate;
    const labor = combineLabor ? getLabourPrice(formValue) : 0;
    const t = editable ? base : base + labor;
    const value = format ? Formatter.formatCurrency(t, getCurrentCompany().currencyCode) : t;

    if (formValue.frequency === DealIntervalResponse.onetime) {
      return value;
    } else {
      return `${value}/${formValue.frequency}`;
    }
  }
}

export function getSubTotalNumber(formValue: LineItemFormData, editable = true) {
  if (formValue.packageItems && formValue.packageItems.length > 0) {
    return formValue.packageItems.reduce((acc, item) => {
      const total = acc + (!editable ? item.qty * item.rate : item.qty * item.rate + getLabourPrice(item));
      return R.isNaN(total) ? 0 : total;
    }, 0);
  } else {
    if (!editable) {
      const tt = formValue.selected ? formValue.qty * formValue.rate : 0;
      return R.isNaN(tt) ? 0 : tt;
    }

    if (formValue.selected) {
      const tt = formValue.qty * formValue.rate + getLabourPrice(formValue);
      return R.isNaN(tt) ? 0 : tt;
    }

    return 0;

    const tt = (formValue.selected ? formValue.qty * formValue.rate : 0) + getLabourPrice(formValue);
    return R.isNaN(tt) ? 0 : tt;
  }
}

export function getGrandTotal(items: LineItemFormData[], editable = true) {
  const t = items.reduce((acc, item) => acc + getSubTotalNumber(item, editable), 0);
  return t;
}

export function getFeeTotal(items: PriceFeeFormData[]) {
  const checkedItems = items.filter(item => item.isChecked);
  const t = checkedItems.reduce((acc, item) => acc + R.toNumber(item.percent), 0);
  return t;
}

export function getFeeTotalAmount(items: PriceFeeFormData[], lineItems: LineItemFormData[]) {
  const checkedItems = items.filter(item => item.isChecked || (item.isChecked as any) == 'true');
  const t = checkedItems.reduce((acc, item) => {
    const feeType = item.feeType == TMFeeType.item ? false : true;
    const subTot = lineItems.reduce((car, o) => car + (getSubTotalNumber(o, feeType) as number), 0);
    const fTotal = (subTot / 100) * item.percent;
    return acc + fTotal;
  }, 0);
  return t;
}

export function getValidDepositAmount(items: LineItemFormData[]) {
  const nonRecurringTotal = items.reduce(
    (acc, item) => acc + (item.frequency != DealIntervalResponse.onetime && item.selected ? item.qty * item.rate : 0),
    0,
  );
  const noRecurringLaborTotal = items.reduce(
    (acc, item) => acc + (item.frequency != DealIntervalResponse.onetime && item.selected ? getLabourPrice(item) : 0),
    0,
  );
  const total = R.isNumber(noRecurringLaborTotal) ? noRecurringLaborTotal + nonRecurringTotal : nonRecurringTotal;
  return R.roundDecimal(total, 2);
}

export function getDiscountAmount(discount: ProposalDiscountFormData, amount: number) {
  if (discount.type === ProposalDiscountType.percentage) {
    return (amount * discount.value) / 100;
  } else {
    return discount.value ?? 0;
  }
}

export function getLineItemLaborItem(item: LineItemFormData) {
  const lItems = (item.labourItems ? item.labourItems : []) as ExtendedLineItemFormData[];
  lItems.forEach((lItem, lItemIndex) => {
    lItems[lItemIndex].total = R.roundDecimal((lItem['price'] ?? 0) * (lItem['hour'] ?? 0) * (item['qty'] ?? 1), 2);
  });
  return lItems;
}

export function getAllLineItems(lineItemsValues: LineItemFormData[]) {
  let uniqueLaborCost = 0;

  if (lineItemsValues.length === 0) {
    return uniqueLaborCost;
  }

  //   const labourItems = lineItemsValues.map(item => {
  //     if (item.packageItems && item.packageItems.length > 0) {
  //       return item.packageItems.map(i => {
  //         // return all labor items
  //         return [...getLineItemLaborItem(i)];
  //         // return getLineItemLaborItem(i)[0];
  //       });
  //     }
  //     return getLineItemLaborItem(item);
  //   });

  const allLaborItems = [] as ExtendedLineItemFormData[];
  lineItemsValues.forEach(item => {
    if (item.packageItems && item.packageItems.length > 0) {
      item.packageItems.forEach(i => {
        allLaborItems.push(...getLineItemLaborItem(i));
      });
    } else {
      allLaborItems.push(...getLineItemLaborItem(item));
    }
  });

  const lineItemPrices = R.flatten(allLaborItems);

  if (allLaborItems.length === 0) {
    return uniqueLaborCost;
  }
  const newPrices = R.filter(lineItemPrices, item => R.get(item, 'price')) as ExtendedLineItemFormData[];

  newPrices.forEach(value => {
    const price = value.price;
    const hour = value.hour;
    const total = value.total ?? price * hour;
    uniqueLaborCost = uniqueLaborCost + total;
  });

  return uniqueLaborCost;
}

export function getTotalQtyAddProd(item: Partial<OrderLineItemData>) {
  if (item.packageItems && item.packageItems.length > 0) {
    return (item.packageItems ?? []).reduce((acc, item) => acc + R.roundDecimal(item.quantity, 2), 0);
  } else {
    return item.quantity;
  }
}

export function getTotalProposalLaborHoursAddProd(item: Partial<LineItemFormData>) {
  if (item.packageItems && item.packageItems.length > 0) {
    return (item.packageItems ?? []).reduce(
      //   (acc, item) => acc + (item.labourItems ?? []).reduce((total, item) => total + R.roundDecimal(item.hour, 2), 0),
      (acc, it) => acc + (it.labourItems ?? []).reduce((total, i) => total + R.roundDecimal(i.hour, 3) * (R.toNumber(it.qty) || 0), 0),
      0,
    );
  } else {
    const items = item.labourItems || [];
    return items.reduce((total, i) => total + R.roundDecimal(i.hour, 3) * (R.toNumber(item.qty) || 0), 0);
    // return items.reduce((total, item) => total + R.roundDecimal(item.hour, 2), 0);
  }
}

export function getTotalLaborHoursAddProd(item: Partial<OrderLineItemData>) {
  const labourItems = item.labourItems || [];
  if (item.packageItems && item.packageItems.length > 0) {
    // if (labourItems.length == 0) {
    //   return (item.packageItems ?? []).reduce((acc, item) => acc + R.roundDecimal(item.labour ?? 0, 2), 0);
    // }
    return (item.packageItems ?? []).reduce(
      (acc, it) => acc + (it.labourItems ?? []).reduce((total, i) => total + R.roundDecimal(i.hour, 3) * (it.quantity || 0), 0),
      0,
    );
  } else {
    const items = item.labourItems || [];
    return items.reduce((total, i) => total + R.roundDecimal(i.hour, 3) * (item.quantity || 0), 0);
  }
}

export function getTotalLaborPriceAddProd(item: Partial<OrderLineItemData>) {
  return (item.packageItems ?? []).reduce((acc, item) => acc + getLabourPriceAddProd(item), 0);
}

export function getProposalPartsPrice(item: Partial<LineItemFormData>) {
  if (item.packageItems && item.packageItems.length > 0) {
    return item.packageItems.reduce((acc, item) => acc + R.roundDecimal(item.qty * item.rate, 3), 0);
  } else {
    return R.roundDecimal((item.qty ?? 0) * (item.rate ?? 0), 3);
  }
}

export function getPartsPrice(item: Partial<OrderLineItemData>) {
  if (item.packageItems && item.packageItems.length > 0) {
    return item.packageItems.reduce((acc, item) => acc + R.roundDecimal(item.quantity * item.rate, 3), 0);
  } else {
    return R.roundDecimal((item.quantity ?? 0) * (item.rate ?? 0), 3);
  }
}

export function getProposalPartsCost(item: Partial<LineItemFormData>) {
  if (item.packageItems && item.packageItems.length > 0) {
    return item.packageItems.reduce((acc, item) => acc + R.roundDecimal(item.qty * convertToValidNumber(item.cost || 0), 3), 0);
  } else {
    return R.roundDecimal((item.qty ?? 0) * (convertToValidNumber(item.cost || 0) ?? 0), 3);
  }
}

export function getPartsCost(item: Partial<OrderLineItemData>) {
  if (item.packageItems && item.packageItems.length > 0) {
    return item.packageItems.reduce((acc, item) => acc + R.roundDecimal(item.quantity * convertToValidNumber(item.cost || 0), 3), 0);
  } else {
    return R.roundDecimal((item.quantity ?? 0) * (convertToValidNumber(item.cost || 0) ?? 0), 3);
  }
}

export function getProposalLabourPriceAddProd(item: Partial<LineItemFormData>) {
  const labourItems = item.labourItems ? item.labourItems : [];
  if (item.packageItems && item.packageItems.length > 0) {
    return item.packageItems.reduce((acc, item) => {
      const labourItems = item.labourItems ? item.labourItems : [];
      if (labourItems.length) {
        const totalPriceHours = labourItems.reduce((total, item) => total + R.roundDecimal(item.hour * item.price, 3), 0);
        return acc + R.roundDecimal((item.qty ?? 0) * totalPriceHours, 3);
      } else {
        const labour = item.labour ?? 0;
        const labourPrice = item.labourPrice ?? 0;
        return acc + R.roundDecimal((item.qty ?? 0) * labour * labourPrice, 3);
      }
    }, 0);
  } else if (labourItems.length) {
    const totalPriceHours = labourItems.reduce((total, item) => total + R.roundDecimal(item.hour * item.price, 3), 0);
    return R.roundDecimal((item.qty ?? 0) * totalPriceHours, 3);
  } else {
    const labour = item.labour ?? 0;
    const labourPrice = item.labourPrice ?? 0;
    return R.roundDecimal((item.qty ?? 0) * labour * labourPrice, 3);
  }
}

export function getLabourPriceAddProd(item: Partial<OrderLineItemData>) {
  const labourItems = item.labourItems ? item.labourItems : [];

  if (item.packageItems && item.packageItems.length > 0) {
    return item.packageItems.reduce((acc, item) => {
      const labourItems = item.labourItems ? item.labourItems : [];
      if (labourItems.length) {
        const totalPriceHours = labourItems.reduce((total, item) => total + R.roundDecimal(item.hour * item.price, 3), 0);
        return acc + R.roundDecimal((item.quantity ?? 0) * totalPriceHours, 3);
      } else {
        const labour = item.labour ?? 0;
        const labourPrice = item.labourPrice ?? 0;
        return acc + R.roundDecimal((item.quantity ?? 0) * labour * labourPrice, 3);
      }
    }, 0);
  } else if (labourItems.length) {
    const totalPriceHours = labourItems.reduce((total, item) => total + R.roundDecimal(item.hour * item.price, 3), 0);
    return R.roundDecimal((item.quantity ?? 0) * totalPriceHours, 3);
  } else {
    const labour = item.labour ?? 0;
    const labourPrice = item.labourPrice ?? 0;
    return R.roundDecimal((item.quantity ?? 0) * labour * labourPrice, 3);
  }
}

export function getLaborServicePrice(item: Partial<OrderLineItemData>) {
  return R.roundDecimal((item.quantity ?? 0) * (item.rate ?? 0), 3);
}

export function getProposalLabourCostAddProd(item: Partial<LineItemFormData>) {
  if (item.packageItems && item.packageItems.length > 0) {
    return item.packageItems.reduce((acc, item) => {
      const totalCostHours = (item.labourItems ?? []).reduce(
        (total, item) => total + R.roundDecimal(item.hour * convertToValidNumber(item.cost || 0), 3),
        0,
      );
      return acc + R.roundDecimal((item.qty ?? 0) * totalCostHours, 3);
    }, 0);
  } else {
    const totalCostHours = (item.labourItems ?? []).reduce(
      (total, item) => total + R.roundDecimal(item.hour * convertToValidNumber(item.cost || 0), 3),
      0,
    );
    return R.roundDecimal((item.qty ?? 0) * totalCostHours, 3);
  }
}

export function getLabourCostAddProd(item: Partial<OrderLineItemData>) {
  if (item.packageItems && item.packageItems.length > 0) {
    return item.packageItems.reduce((acc, item) => {
      const totalCostHours = (item.labourItems ?? []).reduce(
        (total, item) => total + R.roundDecimal(item.hour * convertToValidNumber(item.cost || 0), 3),
        0,
      );
      return acc + R.roundDecimal((item.quantity ?? 0) * totalCostHours, 3);
    }, 0);
  } else {
    const totalCostHours = (item.labourItems ?? []).reduce(
      (total, item) => total + R.roundDecimal(item.hour * convertToValidNumber(item.cost || 0), 3),
      0,
    );
    return R.roundDecimal((item.quantity ?? 0) * totalCostHours, 3);
  }
}

export function getServiceLaborCost(item: Partial<OrderLineItemData>) {
  return R.roundDecimal((item.quantity ?? 0) * (convertToValidNumber(item.cost || 0) ?? 0), 3);
}

export function calculateTotalSalesTax(items: LineItemFormData[], taxRate: number, fees: PriceFeeFormData[]) {
  const t = items.filter(i => i.taxable).reduce((acc, item) => acc + getSubTotalNumber(item), 0);
  // const taxableFeeTotalPercentage = fees.filter(i => i.isChecked && i.taxable).reduce((acc, item) => acc + item.percent, 0);
  // const taxableFeeTotal = (t / 100) * taxableFeeTotalPercentage;
  const checkedAndTaxableFees = fees.filter(i => i.isChecked && i.taxable);
  const taxableFeeTotal = getFeeTotalAmount(checkedAndTaxableFees, items);
  const newT = t + taxableFeeTotal;
  return R.roundDecimal((newT / 100) * taxRate, 2);
}

export function getTaxAmt(items: LineItemFormData[], salesTax: TmInvoiceSalesTaxData, discount: ProposalDiscountFormData, fees: PriceFeeFormData[]) {
  const allLineItems = items;
  const allPackageItems = R.flatten(allLineItems.map(l => l.packageItems));
  let taxableItems = [
    ...allLineItems.map(o => {
      return { ...o, packageItems: [] };
    }),
    ...allPackageItems.map(o => {
      return { ...o, packageItems: [] };
    }),
  ];

  taxableItems = taxableItems.filter(l => {
    if (!l.service) return true;
    return !l.service.includes('package');
  });

  const parsedItems = taxableItems.map(l => ({ taxable: l.taxable, subtotal: getSubTotalNumber(l) }));
  const checkedItems = fees.filter(item => item.isChecked || (item.isChecked as any) == 'true');
  checkedItems.map(item => {
    const feeType = item.feeType == TMFeeType.item ? false : true;
    const subTot = items.reduce((car, o) => car + (getSubTotalNumber(o, feeType) as number), 0);
    const fTotal = (subTot / 100) * item.percent;
    parsedItems.push({ taxable: item.taxable, subtotal: fTotal });
  }, 0);
  const isDiscountByLabor = discount.category == ProposalDiscountCategory.labor;
  const isDiscountByItems = discount.category == ProposalDiscountCategory.items;

  let discountAmt = 0;
  if (discount.isDiscount && !R.isEmpty(discount.category)) {
    const discountType = discount.type;
    const discValue = discount.value;
    if (discountType === 'fixed') {
      discountAmt = R.toNumber(discValue);
    } else {
      const filteredData = discountableRows(R.cloneDeep(items));
      if (isDiscountByLabor) {
        discountAmt = R.toNumber(getProposalTotalLaborPrice(filteredData, discount));
      } else if (isDiscountByItems) {
        discountAmt = R.toNumber(getProposalTotalPartsPrice(filteredData, discount));
      } else {
        discountAmt = calculateGrandTotalDiscount(filteredData, true, discount);
      }
    }
    discount = {
      ...discount,
      type: ProposalDiscountType.fixed,
      value: discountAmt,
    };
  }

  return TaxService.totalTaxAmt(parsedItems, discount, salesTax, true);
}

export function getTotalRateAddProd(item: Partial<OrderLineItemData>) {
  if (item.packageItems && item.packageItems.length > 0) {
    return (item.packageItems ?? []).reduce((acc, item) => acc + R.roundDecimal(item.rate, 3), 0);
  } else {
    return item.rate;
  }
}

export function getCOSubTotal(formValue: ChangeOrderLineItemData, format = true, feeType?: TMFeeType | null) {
  if (formValue.packageItems && formValue.packageItems.length > 0) {
    const t = formValue.packageItems.reduce((acc, item) => {
      if (!item.lineId) {
        return acc + item.quantity * item.rate + (!feeType || feeType == TMFeeType.itemandlabor ? getCOLabourPrice(item) : 0);
      } else if (item.changeQantity <= 0) {
        return acc + item.changeQantity * item.rate + (!feeType || feeType == TMFeeType.itemandlabor ? getCOLabourPrice(item) : 0);
      } else if (item.changeQantity > 0) {
        const differenceOfQuantity = item.changeQantity;
        return acc + differenceOfQuantity * item.rate + (!feeType || feeType == TMFeeType.itemandlabor ? getCOLabourPrice(item) : 0);
      } else {
        return 0;
      }
    }, 0);
    const value = format ? Formatter.formatCurrency(t, 'USD') : t;
    return value;
  } else {
    if (!formValue.lineId) {
      const t = formValue.quantity * formValue.rate + (!feeType || feeType == TMFeeType.itemandlabor ? getCOLabourPrice(formValue) : 0);
      const value = format ? Formatter.formatCurrency(t, 'USD') : t;
      return value;
    } else if (formValue.changeQantity <= 0) {
      const t = formValue.changeQantity * formValue.rate + (!feeType || feeType == TMFeeType.itemandlabor ? getCOLabourPrice(formValue) : 0);
      const value = format ? Formatter.formatCurrency(t, 'USD') : t;
      return value;
    } else if (formValue.changeQantity > 0) {
      const differenceOfQuantity = formValue.changeQantity;
      const t = differenceOfQuantity * formValue.rate + (!feeType || feeType == TMFeeType.itemandlabor ? getCOLabourPrice(formValue) : 0);
      const value = format ? Formatter.formatCurrency(t, 'USD') : t;
      return value;
    } else {
      return 0;
    }
  }
}

export function getCOFeeTotalAmount(items: PriceFeeFormData[], lineItems: ChangeOrderLineItemData[]) {
  const checkedItems = items.filter(item => item.isChecked || (item.isChecked as any) == 'true');
  const t = checkedItems.reduce((acc, item) => {
    const subTot = lineItems.reduce((car, o) => car + (getCOSubTotal(o, false, item.feeType) as number), 0);
    const fTotal = (subTot / 100) * item.percent;
    return acc + fTotal;
  }, 0);
  return t;
}

export function getProposalSubTotalAddProd(formValue: LineItemFormData, format = true) {
  if (formValue.packageItems && formValue.packageItems.length > 0) {
    const t = formValue.packageItems.reduce((acc, item) => acc + item.qty * item.rate + getProposalLabourPriceAddProd(item), 0);
    const value = format ? Formatter.formatCurrency(t, 'USD') : t;
    return value;
  } else {
    const t = formValue.qty * formValue.rate + getProposalLabourPriceAddProd(formValue);
    const value = format ? Formatter.formatCurrency(t, 'USD') : t;
    return value;
  }
}

// export function getCOPartsCost(item: ChangeOrderLineItemData) {
//   if (item.packageItems && item.packageItems.length > 0) {
//     return item.packageItems.reduce((acc, item) => acc + R.roundDecimal((item.quantity + item.changeQantity) * item.cost, 2), 0);
//   } else {
//     return R.roundDecimal((item.quantity + item.changeQantity) * (item.cost ?? 0), 2);
//   }
// }

export function getCOLabourCost(item: TMChangeOrderLineResponse): number {
  if (item.packageItems && item.packageItems.length > 0) {
    return item.packageItems.reduce((acc, item) => {
      const totalCostHours = (item.labourItems ?? []).reduce(
        (total, item) => total + R.roundDecimal(item.hour * convertToValidNumber(item.cost || 0), 2),
        0,
      );
      return acc + R.roundDecimal(item.quantity * totalCostHours, 3);
    }, 0);
  } else {
    const totalCostHours = (item.labourItems ?? []).reduce(
      (total, item) => total + R.roundDecimal(item.hour * convertToValidNumber(item.cost || 0), 2),
      0,
    );
    return R.roundDecimal(item.quantity * totalCostHours, 3);
  }
}

// export function getCOPartsPrice(item: ChangeOrderLineItemData) {
//   if (item.packageItems && item.packageItems.length > 0) {
//     return item.packageItems.reduce((acc, item) => acc + R.roundDecimal((item.quantity + item.changeQantity) * item.rate, 2), 0);
//   } else {
//     return R.roundDecimal((item.quantity + item.changeQantity) * (item.rate ?? 0), 2);
//   }
// }

export function getCOLabourPrice(item: ChangeOrderLineItemData) {
  const labourItems = item.labourItems ? item.labourItems : [];

  if (item.packageItems && item.packageItems.length > 0) {
    return item.packageItems.reduce((acc, item) => {
      const labourItems = item.labourItems ? item.labourItems : [];
      if (labourItems.length) {
        const totalPriceHours = labourItems.reduce((total, item) => total + R.roundDecimal(item.hour * item.price, 3), 0);
        if (!item.lineId) {
          return acc + R.roundDecimal(item.quantity * totalPriceHours, 3);
        } else if (item.changeQantity <= 0) {
          return acc + R.roundDecimal(item.changeQantity * totalPriceHours, 3);
        } else if (item.changeQantity > 0) {
          const differenceOfQuantity = item.changeQantity;
          return acc + R.roundDecimal(differenceOfQuantity * totalPriceHours, 3);
        } else {
          return 0;
        }
        // return acc + R.roundDecimal((item.quantity + item.changeQantity) * totalPriceHours, 2);
      } else {
        const labour = item.labour ?? 0;
        const labourPrice = item.labourPrice ?? 0;
        if (!item.lineId) {
          return acc + R.roundDecimal(item.quantity * labour * labourPrice, 3);
        } else if (item.changeQantity <= 0) {
          return acc + R.roundDecimal(item.changeQantity * labour * labourPrice, 3);
        } else if (item.changeQantity > 0) {
          const differenceOfQuantity = item.changeQantity;
          return acc + R.roundDecimal(differenceOfQuantity * labour * labourPrice, 3);
        } else {
          return 0;
        }
      }
    }, 0);
  } else if (labourItems.length) {
    const totalPriceHours = labourItems.reduce((total, item) => total + R.roundDecimal(item.hour * item.price, 3), 0);
    if (!item.lineId) {
      return R.roundDecimal(item.quantity * totalPriceHours, 3);
    } else if (item.changeQantity <= 0) {
      return R.roundDecimal(item.changeQantity * totalPriceHours, 3);
    } else if (item.changeQantity > 0) {
      const differenceOfQuantity = item.changeQantity;
      return R.roundDecimal(differenceOfQuantity * totalPriceHours, 3);
    } else {
      return 0;
    }
  } else {
    const labour = item.labour ?? 0;
    const labourPrice = item.labourPrice ?? 0;
    if (!item.lineId) {
      return R.roundDecimal(item.quantity * labour * labourPrice, 3);
    } else if (item.changeQantity <= 0) {
      return R.roundDecimal(item.changeQantity * labour * labourPrice, 3);
    } else if (item.changeQantity > 0) {
      const differenceOfQuantity = item.changeQantity;
      return R.roundDecimal(differenceOfQuantity * labour * labourPrice, 3);
    } else {
      return 0;
    }
  }
}

export function getSubTotalAddProd(formValue: OrderLineItemData, format = true, feeType?: TMFeeType | null) {
  if (formValue.packageItems && formValue.packageItems.length > 0) {
    const t = formValue.packageItems.reduce(
      (acc, item) => acc + item.quantity * item.rate + (!feeType || feeType == TMFeeType.itemandlabor ? getLabourPriceAddProd(item) : 0),
      0,
    );
    const value = format ? Formatter.formatCurrency(t, 'USD') : t;
    return value;
  } else {
    const t = formValue.quantity * formValue.rate + (!feeType || feeType == TMFeeType.itemandlabor ? getLabourPriceAddProd(formValue) : 0);
    const value = format ? Formatter.formatCurrency(t, 'USD') : t;
    return value;
  }
}

export function getSubTotalEstimate(formValue: EstimateLineItem, format = true, incLabor = true, onlyTaxable = false) {
  if (formValue.packageItems && formValue.packageItems.length > 0) {
    if (onlyTaxable) {
      const t = formValue.packageItems.reduce(
        (acc, item) => acc + (item.taxable ? item.quantity * item.unitPrice + (incLabor ? getLabourPriceEstimate(item) : 0) : 0),
        0,
      );
      const value = format ? Formatter.formatCurrency(t, 'USD') : t;
      return value;
    } else {
      const t = formValue.packageItems.reduce((acc, item) => acc + item.quantity * item.unitPrice + (incLabor ? getLabourPriceEstimate(item) : 0), 0);
      const value = format ? Formatter.formatCurrency(t, 'USD') : t;
      return value;
    }
  } else {
    const t = formValue.quantity * formValue.unitPrice + (incLabor ? getLabourPriceEstimate(formValue) : 0);
    const value = format ? Formatter.formatCurrency(t, 'USD') : t;
    return value;
  }
}

export function getTotalRateEstimate(item: Partial<EstimateLineItem>) {
  if (item.packageItems && item.packageItems.length > 0) {
    return (item.packageItems ?? []).reduce((acc, item) => acc + R.roundDecimal(item.unitPrice, 3), 0);
  } else {
    return item.unitPrice;
  }
}

export function getLabourPriceEstimate(item: Partial<EstimateLineItem>) {
  const labourItems = item.labourItems ? item.labourItems : [];

  if (item.packageItems && item.packageItems.length > 0) {
    return item.packageItems.reduce((acc, item) => {
      const labourItems = item.labourItems ? item.labourItems : [];
      if (labourItems.length) {
        const totalPriceHours = labourItems.reduce((total, item) => total + R.roundDecimal(item.hour * item.price, 3), 0);
        return acc + R.roundDecimal((item.quantity ?? 0) * totalPriceHours, 3);
      } else {
        return 0;
      }
    }, 0);
  } else if (labourItems.length) {
    const totalPriceHours = labourItems.reduce((total, item) => total + R.roundDecimal(item.hour * item.price, 3), 0);
    return R.roundDecimal((item.quantity ?? 0) * totalPriceHours, 3);
  } else {
    return 0;
  }
}

export function getTotalLaborHoursEstimate(item: Partial<EstimateLineItem>) {
  if (item.packageItems && item.packageItems.length > 0) {
    return (item.packageItems ?? []).reduce(
      (acc, item) => acc + (item.labourItems ?? []).reduce((total, item) => total + R.roundDecimal(item.hour, 2), 0),
      0,
    );
  } else {
    const items = item.labourItems || [];
    return items.reduce((total, item) => total + R.roundDecimal(item.hour, 2), 0);
  }
}

export function getTotalQtyEstimate(item: Partial<EstimateLineItem>) {
  if (item.packageItems && item.packageItems.length > 0) {
    return (item.packageItems ?? []).reduce((acc, item) => acc + R.roundDecimal(item.quantity, 2), 0);
  } else {
    return item.quantity;
  }
}

export function getTotalAfterCardFee(
  settings: SettingsResponse | null,
  proposalAmount: number,
  amount: number,
  depositType = 'fixed',
  addCcFee = true,
  subscriptionAmount = 0,
) {
  if (!settings) {
    return amount;
  }
  let newAmount = amount;

  if (depositType === 'percentage') {
    newAmount = subscriptionAmount + ((proposalAmount - subscriptionAmount) / 100) * amount;
  }

  if (!addCcFee) {
    return R.round(newAmount, 2);
  }

  const isCreditCardProcessingFee = settings && settings.isCreditCardProcessingFee;
  const creditCardProcessingFee = (settings && R.toNumber(settings.creditCardProcessingFee)) || 0;
  if (isCreditCardProcessingFee && newAmount) {
    const processingFee = R.toNumber(((newAmount / 100) * creditCardProcessingFee).toFixed(2));
    const grandTotal = processingFee + newAmount;
    return R.round(grandTotal || 0, 2);
  } else {
    return R.round(newAmount, 2);
  }
}

export function getProposalTotalLaborPrice(products: LineItemFormData[], discount?: ProposalDiscountFormData) {
  return products.reduce((total, line) => {
    const propLaborPrice = getProposalLabourPriceAddProd(line);
    if (discount) {
      const discountedAmount = getDiscountAmount(discount, propLaborPrice);
      return R.toNumber(total) + R.toNumber(discountedAmount);
    } else {
      return R.toNumber(total) + R.toNumber(propLaborPrice);
    }
  }, 0);
}

export function getProposalTotalPartsPrice(products: LineItemFormData[], discount?: ProposalDiscountFormData) {
  return products.reduce((total, line) => {
    const propPartsPrice = getProposalPartsPrice(line);
    if (discount) {
      const discountedAmount = getDiscountAmount(discount, propPartsPrice);
      return R.toNumber(total) + R.toNumber(discountedAmount);
    } else {
      return R.toNumber(total) + R.toNumber(propPartsPrice);
    }
  }, 0);
}

export function calculateGrandTotalDiscount(items: LineItemFormData[], editable = true, discount: ProposalDiscountFormData) {
  const t = items.reduce((acc, item) => acc + getSubTotalNumber(item, editable), 0);
  return getDiscountAmount(discount, t);
}

export function calculateGrandTotalDiscountEditable(items: LineItemFormData[], editable = true, discount: ProposalDiscountFormData) {
  const t = items.reduce((acc, item) => acc + getSubTotalNumber(item, true), 0);
  return getDiscountAmount(discount, t);
}

export function discountableRows(items: LineItemFormData[]) {
  return items.filter(item => {
    if (!item.isDiscount && item.packageItems.length == 0) {
      // if product
      //   item.packageItems = item.packageItems.filter(packageItem => !packageItem.isDiscount);
      return item;
    } else if (item.packageItems.length > 0) {
      // if package
      item.packageItems = item.packageItems.filter(packageItem => !packageItem.isDiscount);
      return item;
    }
  });
}

export function getCOSubTotalTaxAmt(formValue: ChangeOrderLineItemData) {
  if (!formValue.lineId) {
    const t = formValue.quantity * formValue.rate + getCOLabourPrice(formValue);
    return t;
  } else if (formValue.changeQantity <= 0) {
    const t = formValue.changeQantity * formValue.rate + getCOLabourPrice(formValue);
    return t;
  } else if (formValue.changeQantity > 0) {
    const differenceOfQuantity = formValue.changeQantity;
    const t = differenceOfQuantity * formValue.rate + getCOLabourPrice(formValue);
    return t;
  } else {
    return 0;
  }
  // }
}

export function getCOSubTotalTaxAmtItems(items: ChangeOrderLineItemData[], taxPercent: number) {
  const allLineItems = items;
  const allPackageItems = R.flatten(allLineItems.map(l => l.packageItems));

  const collectiveItems = [...allLineItems, ...allPackageItems];
  const amt = collectiveItems.filter(p => !!p.taxable).reduce((total, line) => total + R.roundDecimal(getCOSubTotalTaxAmt(line), 2), 0);
  if (taxPercent) {
    return R.roundDecimal((amt * taxPercent) / 100, 2);
  }
  return 0;
}

export function convertToValidNumber(number: string | number) {
  if (R.isString(number)) {
    number = number.replace('$', '');
  }

  if (R.isString(number) && number === '.') {
    return 0;
  }

  return R.toNumber(number);
}

export interface CommissionSplitSetting {
  userId: string;
  percentage: number;
}

/**
 * Commission share to show for one sales rep of a proposal.
 *
 * Reps that have a configured split keep their configured share and the remaining reps divide
 * what is left. When nothing is configured, or the configuration is unusable (it overruns 100%,
 * or every rep is configured but the shares don't add up to 100), the split is even — the same
 * fallback the backend's commission-split notification describes.
 *
 * Returns a number so callers decide the formatting. Never throws on a missing split.
 */
export function getSaleRepPercentage(salesReps: (string | number)[], splits: CommissionSplitSetting[], saleRepId: string | number): number {
  const reps = R.uniq((salesReps || []).filter(id => !R.isNil(id) && id !== '').map(id => String(id)));
  if (reps.length === 0) {
    return 0;
  }
  if (reps.length === 1) {
    return 100;
  }

  const evenSplit = 100 / reps.length;
  const splitOf = (id: string) => (splits || []).find(split => String(split.userId) === id);

  const configured = reps.filter(id => !!splitOf(id));
  if (configured.length === 0) {
    return evenSplit;
  }

  const configuredTotal = configured.reduce((total, id) => total + R.toNumber(splitOf(id)!.percentage), 0);
  const unconfiguredCount = reps.length - configured.length;
  if (configuredTotal > 100 || (unconfiguredCount === 0 && configuredTotal !== 100)) {
    return evenSplit;
  }

  const ownSplit = splitOf(String(saleRepId));
  if (ownSplit) {
    return R.toNumber(ownSplit.percentage);
  }
  return unconfiguredCount > 0 ? (100 - configuredTotal) / unconfiguredCount : evenSplit;
}
